document.documentElement.classList.remove('no-js');

class ProductPersonalizer {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.variantInput = this.form?.querySelector('[name="id"]');
    this.variants = JSON.parse(root.querySelector('[data-variants-json]')?.textContent || '[]');
    this.currency = root.dataset.currency || 'USD';
    this.uploadMode = root.dataset.uploadMode || 'native';
    this.maxMb = Number(root.dataset.maxMb || 5);
    this.cloudName = root.dataset.cloudName || '';
    this.uploadPreset = root.dataset.uploadPreset || '';
    this.cloudFolder = root.dataset.cloudFolder || 'pet-portraits';
    this.showPriceAnchor = root.dataset.showPriceAnchor !== 'false';
    this.photoSubmission = 'now';
    this.petCount = 1;
    this.pendingUploads = 0;
    this.bind();
    this.syncVariant();
  }

  bind() {
    this.root.querySelectorAll('[data-option-input]').forEach(input => {
      input.addEventListener('change', () => this.syncVariant());
    });
    this.root.querySelectorAll('[data-pet-count]').forEach(input => {
      input.addEventListener('change', () => this.syncUploadSlots(Number(input.dataset.petCount)));
      if (input.checked) this.syncUploadSlots(Number(input.dataset.petCount));
    });
    this.root.querySelectorAll('[data-photo-submission]').forEach(input => {
      input.addEventListener('change', () => this.syncPhotoSubmission(input.dataset.photoSubmission));
      if (input.checked) this.syncPhotoSubmission(input.dataset.photoSubmission);
    });
    this.root.querySelectorAll('[data-photo-input]').forEach(input => {
      input.addEventListener('change', event => this.handleFile(event.currentTarget));
    });
    this.form?.addEventListener('submit', event => {
      if (this.photoSubmission === 'later') return;
      const required = [...this.root.querySelectorAll('[data-upload-slot]:not(.is-hidden) [data-photo-input]')];
      const missing = required.some(input => !input.files?.length && !input.closest('[data-upload-slot]').querySelector('[data-cloud-url]')?.value);
      if (missing || this.pendingUploads > 0) {
        event.preventDefault();
        this.showError(this.pendingUploads ? 'Please wait for your photo upload to finish.' : 'Please upload one clear photo for each pet.');
      }
    });
  }

  syncUploadSlots(count) {
    const safeCount = Math.max(1, Math.min(3, Number(count) || 1));
    this.petCount = safeCount;
    const uploadNow = this.photoSubmission === 'now';
    this.root.querySelectorAll('[data-upload-slot]').forEach((slot, index) => {
      const isActive = index < safeCount;
      slot.classList.toggle('is-hidden', !isActive);
      const input = slot.querySelector('[data-photo-input]');
      if (input) {
        input.required = isActive && uploadNow;
        input.disabled = !isActive || !uploadNow;
      }
      slot.querySelectorAll('[data-cloud-url]').forEach(field => { field.disabled = !isActive || !uploadNow; });
    });
    const note = this.root.querySelector('[data-upload-count-note]');
    if (note) note.textContent = `${safeCount} ${safeCount === 1 ? 'photo' : 'photos'} required for ${safeCount} ${safeCount === 1 ? 'pet' : 'pets'}.`;
  }

  syncPhotoSubmission(mode) {
    this.photoSubmission = mode === 'later' ? 'later' : 'now';
    const uploadNow = this.photoSubmission === 'now';
    const uploadPanel = this.root.querySelector('[data-photo-upload-panel]');
    const laterPanel = this.root.querySelector('[data-photo-later-panel]');
    const photoStatus = this.root.querySelector('[data-photo-status]');
    if (uploadPanel) uploadPanel.hidden = !uploadNow;
    if (laterPanel) laterPanel.hidden = uploadNow;
    if (photoStatus) photoStatus.disabled = uploadNow;
    this.syncUploadSlots(this.petCount);
    this.showError('');
  }

  selectedOptions() {
    return [...this.root.querySelectorAll('[data-product-option]')].map(group => group.querySelector('[data-option-input]:checked')?.value);
  }

  syncVariant() {
    if (!this.variants.length) return;
    const selected = this.selectedOptions();
    const variant = selected.length === 0 && this.variants.length === 1
      ? this.variants[0]
      : this.variants.find(item => item.options.every((value, index) => value === selected[index]));
    const button = this.form?.querySelector('[data-add-button]');
    if (!variant) {
      if (button) {
        button.disabled = true;
        button.textContent = 'Unavailable';
      }
      return;
    }
    this.variantInput.value = variant.id;
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
    this.root.querySelectorAll('[data-current-price]').forEach(element => {
      element.textContent = this.money(variant.price);
    });
    const comparePrice = this.root.querySelector('[data-compare-price]');
    const offerBadge = this.root.querySelector('[data-offer-badge]');
    const variantComparePrice = Number(variant.compare_at_price || 0);
    const useVariantCompare = this.showPriceAnchor && variantComparePrice > variant.price;
    const showComparePrice = useVariantCompare;
    if (comparePrice) {
      comparePrice.hidden = !showComparePrice;
      if (showComparePrice) comparePrice.textContent = this.money(variantComparePrice);
    }
    if (offerBadge) {
      offerBadge.hidden = !showComparePrice;
      if (showComparePrice) offerBadge.textContent = `${Math.round(((variantComparePrice - variant.price) / variantComparePrice) * 100)}% off`;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.currency }).format(cents / 100);
  }

  async handleFile(input) {
    const file = input.files?.[0];
    if (!file) return;
    const slot = input.closest('[data-upload-slot]');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) return this.showError('Please choose a JPG, PNG, or WebP image.');
    if (file.size > this.maxMb * 1024 * 1024) return this.showError(`Each photo must be ${this.maxMb} MB or smaller.`);
    slot.querySelector('[data-preview]').src = URL.createObjectURL(file);
    slot.querySelector('[data-file-name]').textContent = file.name;
    slot.classList.add('is-uploaded');
    this.showError('');
    if (this.uploadMode !== 'cloudinary') return;
    if (!this.cloudName || !this.uploadPreset) return this.showError('Cloudinary is selected, but cloud name or unsigned preset is missing in Theme settings.');
    slot.classList.add('is-uploading');
    this.pendingUploads += 1;
    try {
      const payload = new FormData();
      payload.append('file', file);
      payload.append('upload_preset', this.uploadPreset);
      payload.append('folder', this.cloudFolder);
      const response = await fetch(`https://api.cloudinary.com/v1_1/${encodeURIComponent(this.cloudName)}/image/upload`, { method: 'POST', body: payload });
      if (!response.ok) throw new Error('Upload failed');
      const result = await response.json();
      slot.querySelector('[data-cloud-url]').value = result.secure_url;
      input.removeAttribute('name');
    } catch (error) {
      input.value = '';
      slot.classList.remove('is-uploaded');
      this.showError('Photo upload failed. Please try again.');
    } finally {
      this.pendingUploads -= 1;
      slot.classList.remove('is-uploading');
    }
  }

  showError(message) {
    const error = this.root.querySelector('[data-form-error]');
    if (!error) return;
    error.textContent = message;
    error.classList.toggle('is-visible', Boolean(message));
  }
}

function initAnnouncements(scope = document) {
  scope.querySelectorAll('[data-announcement-rotator]').forEach(root => {
    const messages = [...root.querySelectorAll('[data-announcement-message]')];
    if (messages.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let activeIndex = 0;
    window.clearInterval(root.announcementTimer);
    root.announcementTimer = window.setInterval(() => {
      messages[activeIndex].classList.remove('is-active');
      messages[activeIndex].hidden = true;
      activeIndex = (activeIndex + 1) % messages.length;
      messages[activeIndex].hidden = false;
      requestAnimationFrame(() => messages[activeIndex].classList.add('is-active'));
    }, Number(root.dataset.interval || 5000));
  });
}

function initProductGalleries(scope = document) {
  scope.querySelectorAll('[data-product-gallery]').forEach(gallery => {
    if (gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    const thumbs = [...gallery.querySelectorAll('[data-gallery-thumb]')];
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    if (!slides.length) return;
    let activeIndex = Math.max(0, slides.findIndex(slide => !slide.hidden));

    const show = requestedIndex => {
      activeIndex = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        if (!active) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      thumbs.forEach((thumb, index) => {
        const active = index === activeIndex;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-selected', active ? 'true' : 'false');
        thumb.setAttribute('tabindex', active ? '0' : '-1');
      });
      thumbs[activeIndex]?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };

    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => show(index)));
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(activeIndex - 1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(activeIndex + 1));
    gallery.addEventListener('keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      show(activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });
    show(activeIndex);
  });
}

function initMegaMenus(scope = document) {
  scope.querySelectorAll('[data-mega-menu]').forEach(menu => {
    if (menu.dataset.megaReady === 'true') return;
    menu.dataset.megaReady = 'true';
    const trigger = menu.querySelector('[data-mega-trigger]');
    let closeTimer;
    const open = () => {
      window.clearTimeout(closeTimer);
      menu.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      menu.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
    };
    menu.addEventListener('mouseenter', open);
    menu.addEventListener('mouseleave', () => { closeTimer = window.setTimeout(close, 110); });
    menu.addEventListener('focusin', open);
    menu.addEventListener('focusout', event => {
      if (!menu.contains(event.relatedTarget)) close();
    });
    menu.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      close();
      trigger?.focus();
    });
  });

  scope.querySelectorAll('[data-mobile-mega]').forEach(menu => {
    if (menu.dataset.mobileMegaReady === 'true') return;
    menu.dataset.mobileMegaReady = 'true';
    const toggle = menu.querySelector('[data-mobile-mega-toggle]');
    const panel = menu.querySelector('[data-mobile-mega-panel]');
    toggle?.addEventListener('click', () => {
      const willOpen = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (panel) panel.hidden = !willOpen;
    });
  });
}

document.querySelectorAll('[data-product-personalizer]').forEach(root => new ProductPersonalizer(root));
initAnnouncements();
initProductGalleries();
initMegaMenus();

document.addEventListener('shopify:section:load', event => {
  initAnnouncements(event.target);
  initProductGalleries(event.target);
  initMegaMenus(event.target);
});

document.querySelectorAll('[data-menu-toggle]').forEach(button => button.addEventListener('click', () => {
  const nav = button.closest('.site-header')?.querySelector('[data-mobile-nav]');
  nav?.classList.toggle('is-open');
  const isOpen = nav?.classList.contains('is-open') || false;
  button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  button.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
  if (!isOpen) {
    nav?.querySelectorAll('[data-mobile-mega-toggle]').forEach(toggle => toggle.setAttribute('aria-expanded', 'false'));
    nav?.querySelectorAll('[data-mobile-mega-panel]').forEach(panel => { panel.hidden = true; });
  }
}));

document.querySelectorAll('[data-sticky-submit]').forEach(button => button.addEventListener('click', () => {
  document.querySelector('[data-add-button]')?.click();
}));
